/**
 * 可选语言与顺序：由环境变量 VITE_I18N_LOCALES 配置（逗号/空格分隔）。
 * 须与 src/locales 下 json 文件名（不含 .json）一致。
 */
import { env } from './env';

const LANG_STORAGE_KEY = 'lang';

/** 全量展示名：仅 env 中列出的会出现在下拉里 */
export const LOCALE_LABELS = {
    cn: '简体中文',
    'zh-tw': '繁體中文',
    en: 'English',
    ru: 'Русский язык',
    ma: 'Malay',
    ko: '한국어',
    es: 'Español',
    pt: 'Português',
    ja: '日本語',
    vn: 'Tiếng Việt',
    de: 'Deutsch',
    fr: 'Français',
    th: 'ไทย',
    uk: 'Українська',
};

// Same set/order as automac (cn…fr); fr is Android slot 8 on macTypeId 130, else use vn
const DEFAULT_LOCALE_ORDER = Object.keys(LOCALE_LABELS);

/**
 * 解析为有序、去重、仅保留已定义条目的 code 列表
 */
export function parseSupportedLocaleCodes() {
    const raw = env.I18N_LOCALES;
    if (raw == null || String(raw).trim() === '') {
        return [...DEFAULT_LOCALE_ORDER];
    }
    const seen = new Set();
    const out = [];
    String(raw)
        .split(/[,，\s;；]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .forEach((code) => {
            if (LOCALE_LABELS[code] == null) {
                return;
            }
            if (seen.has(code)) {
                return;
            }
            seen.add(code);
            out.push(code);
        });
    return out.length > 0 ? out : [...DEFAULT_LOCALE_ORDER];
}

export function getSupportedLocaleCodes() {
    return parseSupportedLocaleCodes();
}

/**
 * 头部 el-select 选项：顺序与 env 一致
 */
export function getLocaleSelectOptions() {
    return getSupportedLocaleCodes().map((value) => ({
        value,
        label: LOCALE_LABELS[value] || value,
    }));
}

/**
 * 默认回退：优先 I18N_LOCALE（若在支持列表中），否则列表首项
 */
export function getDefaultLang(supported) {
    const s = supported || getSupportedLocaleCodes();
    const preferred = env.I18N_LOCALE;
    if (preferred && s.includes(preferred)) {
        return preferred;
    }
    return s[0] || 'en';
}

function mapBrowserToLocaleCode() {
    const b = String(typeof navigator !== 'undefined'
        && (navigator.language || navigator.browserLanguage || '')).trim().replace(/_/g, '-').toLowerCase();
    if (b.includes('zh-tw') || b.includes('zh-hant') || b.includes('zh-hk') || b.includes('zh-mo')) {
        return 'zh-tw';
    }
    if (b.includes('zh')) {
        return 'cn';
    }
    if (b.includes('ru')) {
        return 'ru';
    }
    if (b.includes('ko')) {
        return 'ko';
    }
    if (b.includes('es')) {
        return 'es';
    }
    if (b.includes('pt')) {
        return 'pt';
    }
    if (b.includes('vi')) {
        return 'vn';
    }
    if (b.includes('ja')) {
        return 'ja';
    }
    if (b.includes('de')) {
        return 'de';
    }
    if (b.includes('fr')) {
        return 'fr';
    }
    if (b.includes('th')) {
        return 'th';
    }
    if (b === 'uk' || b.startsWith('uk-')) {
        return 'uk';
    }
    if (b.includes('ms') || b.includes('ma')) {
        return 'ma';
    }
    return 'en';
}

/**
 * 与 main.js / i18n 初始化共用：读 localStorage → 合法则采用，否则试浏览器，再否则默认
 */
export function resolveAppLocale() {
    const supported = getSupportedLocaleCodes();
    if (typeof window === 'undefined' || !window.localStorage) {
        return getDefaultLang(supported);
    }
    const current = String(window.localStorage.getItem(LANG_STORAGE_KEY) || '').trim().replace(/_/g, '-').toLowerCase();
    const normalizedCurrent = current === 'uk-ua' ? 'uk' : current;
    if (normalizedCurrent && supported.includes(normalizedCurrent)) {
        return normalizedCurrent;
    }
    const fromBrowser = mapBrowserToLocaleCode();
    if (supported.includes(fromBrowser)) {
        return fromBrowser;
    }
    return getDefaultLang(supported);
}
